import { BlendMode } from '@kitware/vtk.js/Rendering/Core/VolumeMapper/Constants';

/**
 * How the 3D view projects the volume. 'mip' = maximum intensity projection over the whole volume
 * (contrast-filled vessels, bone). MinIP / average are left to the OHIF MPR slab tool: over a whole
 * volume the surrounding air dominates and the picture comes out empty.
 */
export type VolumeBlendMode = 'composite' | 'mip';

export function toVtkBlendMode(mode: VolumeBlendMode) {
  return mode === 'mip'
    ? BlendMode.MAXIMUM_INTENSITY_BLEND
    : BlendMode.COMPOSITE_BLEND;
}

const MIP_PRESET_BY_MODALITY: Record<string, string> = {
  CT: 'CT-MIP',
  MR: 'MR-MIP',
};

/** Grayscale preset that reads as a MIP; null = no suitable preset, keep the current one. */
export function mipPresetForModality(modality: string | undefined) {
  return (modality && MIP_PRESET_BY_MODALITY[modality]) || null;
}

export interface BlendModeState {
  blendMode: VolumeBlendMode;
  preset: string;
  presetBeforeMip?: string;
}

export interface BlendModeChange {
  blendMode: VolumeBlendMode;
  applyPreset?: string;
  presetBeforeMip?: string;
}

/**
 * A colour rendering preset (bone, AAA…) under MIP gives a tinted, half-transparent picture, so MIP
 * switches to the grayscale MIP preset and switching back restores what the user had before.
 */
export function planBlendModeChange(
  state: BlendModeState,
  next: VolumeBlendMode,
  modality: string | undefined
): BlendModeChange {
  if (next === state.blendMode) {
    return state.presetBeforeMip === undefined
      ? { blendMode: next }
      : { blendMode: next, presetBeforeMip: state.presetBeforeMip };
  }
  if (next === 'mip') {
    const mipPreset = mipPresetForModality(modality);
    if (!mipPreset || mipPreset === state.preset) {
      return { blendMode: 'mip' };
    }
    return {
      blendMode: 'mip',
      applyPreset: mipPreset,
      presetBeforeMip: state.preset,
    };
  }
  return {
    blendMode: 'composite',
    applyPreset: state.presetBeforeMip,
    presetBeforeMip: undefined,
  };
}
